import React from "react";
import { styled } from "@mui/system";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Heading = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  fontWeight: theme.typography.fontWeightRegular,
}));

export default function ClientCreateDoc() {
  return (
    <div style={{ width: "50%", marginLeft: "3em" }}>
      <h1 data-testid="more-info-heading">More Info</h1>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-client-name">
          <Heading>Client Name</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-client-name-details">
          <Typography>
            <p>The client name is the customer's company name</p>
            <ul>
              <li>This can be found from the environment request form</li>
              <li>This can be altered later</li>
              <li>
                If this is for a sandbox or guest environment, add “ (dev)” or " (guest)" respectively to the ending.
              </li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-client-key">
          <Heading>Client Key</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-client-key-details">
          <Typography>
            <p>The client key should resemble the customer</p>
            <ul>
              <li>
                This <b>CANNOT</b> be altered later
              </li>
              <li>No spaces, only hyphens</li>
              <li>No numbers, all lowercase</li>
              <li>Can't start with a number</li>
              <li>If this is for a sandbox or guest environment, add "-dev" or "-guest" respectively to the ending</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-client-type">
          <Heading>Client Type</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-client-type-details">
          <Typography>
            <p>The type of environment being created</p>
            <ul>
              <li>For types Guest and Sandbox, you must append the proper suffixes to the name and client key</li>
              <li>No suffixes are needed for a Live environment</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-live-client-key">
          <Heading>Live Client Key</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-live-client-key-details">
          <Typography>
            <p>This is the primary environment the client is tied to.</p>
            <ul>
              <li>
                If the client does <b>not</b> have an associated app, this will need to be set for a user's load updates
                and push notifications to function properly.
              </li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-client-group-key">
          <Heading>Client Group Key</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-client-group-key-details">
          <Typography>This key is defined when the client group is created.</Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-hubspot-company-id">
          <Heading>Hubspot Company ID</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-hubspot-company-id-details">
          <Typography>
            <p>The ID of the corresponding Hubspot company.</p>
            <ul>
              <li>
                To find the ID, look up the name of the customer in this{" "}
                <a href="https://app.periscopedata.com/app/eleostech/1280586/Hubspot-Companies" target="_blank">
                  report
                </a>{" "}
              </li>
              <li>
                Please make sure to use the 'name' filter at the top of the page and not the search option under the
                "More options" button.
              </li>
              <li>
                If you cannot find the appropriate company in the report, reach out to the company's account executive
              </li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-hubspot-deal-id">
          <Heading>Hubspot Deal ID</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-hubspot-deal-id-details">
          <Typography>
            <p>The ID of the corresponding Hubspot deal.</p>
            <ul>
              <li>This field should almost always be filled, only in rare circumstances should this be left empty</li>
              <li>
                To find the ID, look up the name of the customer in this{" "}
                <a href="https://app.periscopedata.com/app/eleostech/1280601/Hubspot-Deals" target="_blank">
                  report
                </a>
              </li>
              <li>You will likely want to look for deals that are Closed Won</li>
              <li>
                Please make sure to use the 'name' filter at the top of the page and not the search option under the
                "More options" button.
              </li>
              <li>
                If you cannot find the appropriate deal in the report, reach out to the company's account executive
              </li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-billing-account">
          <Heading>Billing Account</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-billing-account-details">
          <Typography>
            <p>The UUID is the billing account number that is associated with the relevant reseller/house account</p>
            <ul>
              <li>If the appropriate billing account does not appear in the list, let Ryan Crum know</li>
              <li>The UUID list can be found in Periscope</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header" data-testid="accordion-guest-client">
          <Heading>Guest Client</Heading>
        </AccordionSummary>
        <AccordionDetails data-testid="accordion-guest-client-details">
          <Typography>
            <p>This is the environment that a user enters when selecting guest mode at login.</p>
            <ul>
              <li>
                If naming conventions were followed properly, the guest client should have the same name as this client
                but with "(guest)" at the end.
              </li>
              <li>The guest client must be in the same client group as this client.</li>
              <li>The associated guest client must be of type "guest"</li>
              <li>A client cannot be its own guest client.</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
